import React, { useEffect, useRef, useState } from "react";
import { ContentContainer } from "../../../components/layout";
import {
  Button,
  Checkbox,
  DownloadButton,
  LoadingComponent,
  Modal,
  MultiSelect,
  NoDataFoundTemplate,
  Select,
} from "../../../components/core";
import { useLazyQuery, useQuery } from "@apollo/client";
import { GET_OVERSTAY_FEES_SUMMARY_REPORT } from "../../../constants/graphql/overstay-fees-report";
import { LOCKER_INFO } from "../../../constants/graphql/locker-queries";
import {
  formatDate,
  formatDateTime,
  formatDateYYYYMMDD,
  getSixMonthsAgo,
  toUTCDateTimeString,
} from "../../../algorithms/date";
import PrimereactDateRange from "../../../components/core/date-range-primereact";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Card } from "primereact/card";
import { ColumnGroup } from "primereact/columngroup";
import { Row } from "primereact/row";
import {
  QUERY_LOCKER_NAME,
  QUERY_LOCKER_TYPE,
} from "../../../constants/graphql/drop-off-graphql";
import { NEWEST_LOCKER_NAME_QUERY } from "../../../features/stay-time-in-locker/stay-time-query/stay-time-graphql";

const OverstayFeesReportSummary = () => {
  const operatorId = JSON.parse(localStorage.getItem("user")).operator_id;
  const dt = useRef(null);

  // local variables
  const [nameData, setNameData] = useState([]);
  const [allLockersSelected, setAllLockersSelected] = useState(false);
  const [selectedLockerType, setSelectedLockerType] = useState("-1");
  const [lockerName, setLockerName] = useState([]);
  const [lockerType, setLockerType] = useState([]);
  const [selectedLockerName, setSelectedLockerName] = useState([]);

  const maxStartDate = getSixMonthsAgo();

  
  // filter variables
  //   const [customStartDate, setCustomStartDate] = useState(() => {
  //     const today = new Date();
  //     today.setMonth(today.getMonth() - 6);
  //     return today;
  //   });
  //   const [customEndDate, setCustomEndDate] = useState(new Date());
  const [searchResultHeader, setSearchResultHeader] = useState("");

  // table variables
  const [filterButtonClicked, setFilterButtonClicked] = useState(false);
  const [isClicked, setIsClicked] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showMore, setShowMore] = useState(false);
  const [groupedData, setGroupedData] = useState([]);

  const [overstaySummaryTable, setOverstaySummaryTable] = useState([]);
  // const [totalAmt, setTotalAmt] = useState(0);
  // const [totalPackages, setTotalPackages] = useState(0);
  const [totalFees, setTotalFees] = useState(0);
  const [totalPrice, setTotalPrice] = useState(0);
  const [filterVariables, setFilterVariables] = useState({
    startTime: "00:00",
    endTime: "23:59",
    startDate: (() => {
      const d = new Date();
      d.setMonth(d.getMonth() - 6);
      return d;
    })(),
    endDate: new Date(),
  });
  // validation
  const [validation, setValidation] = useState({
    icon: `${process.env.PUBLIC_URL}/assets/images/error.png`,
    title: "",
    message: "",
    open: false,
  });

  // success / error Modal
  const showError = (title, message) => {
    setValidation({
      icon: `${process.env.PUBLIC_URL}/assets/images/error.png`,
      title: title,
      message: message,
      open: true,
      actions: (
        <div className="w-full">
          <Button
            onClick={() => setValidation({ open: false })}
            value={"OK"}
            className={"p-2 w-full"}
          />
        </div>
      ),
    });

    setIsLoading(false);
  };

  // gql
  const [fetchOverstayReportSummary, { data: overstaySummaryLazy, loading }] =
    useLazyQuery(GET_OVERSTAY_FEES_SUMMARY_REPORT);
  const { data: allLockerNames } = useQuery(LOCKER_INFO, {
    variables: {
      operatorId: operatorId,
    },
  });
  const [fetchLockerName, { data: lockerNameData }] = useLazyQuery(
    NEWEST_LOCKER_NAME_QUERY,
    {
      variables: {
        operatorId: operatorId,
      },
    }
  );
  const fetchLockerType = useQuery(QUERY_LOCKER_TYPE);

  const findLockerName = (lockerId, nameData) => {
    const locker = nameData.find(
      (locker) => parseInt(locker.lockerId) === parseInt(lockerId)
    );

    return locker ? locker.lockerName : null;
  };

  useEffect(() => {
    if (fetchLockerType.data) {
      let tempData = [];
      fetchLockerType.data.lockerTypes.forEach((element, index) =>
        tempData.push({
          value: element.lockerTypeId,
          name: element.lockerType,
        })
      );
      setLockerType(tempData);
    }
  }, [fetchLockerType.data]);

  useEffect(() => {
    if (allLockerNames && allLockerNames.lockerInfo) {
      const lockerDetails = allLockerNames.lockerInfo.map((locker) => ({
        lockerId: locker.lockerId,
        lockerName: locker.lockerName,
      }));
      setNameData(lockerDetails);
    }
  }, [allLockerNames]);

  useEffect(() => {
    if (loading) {
      setIsLoading(true);
    }
  }, [loading]);

  useEffect(() => {
    if (selectedLockerName.length !== 0) {
      const hasZeroValue = selectedLockerName.some((item) => item.value === 0);
      if (hasZeroValue) {
        setAllLockersSelected(true);
      }
    }
  }, [selectedLockerName]);

  useEffect(() => {
    if (allLockersSelected) {
      const selectedLockers = lockerName.filter((locker) => locker.value !== 0);

      setSelectedLockerName(selectedLockers);
    } else {
      setSelectedLockerName([]);
    }
  }, [allLockersSelected, lockerName]);

  useEffect(() => {
    if (lockerNameData) {
      const tempData = lockerNameData.lockerInfo.map((element) => ({
        label: element.lockerName,
        value: element.lockerId,
      }));

      if (!allLockersSelected) {
        tempData.unshift({
          value: 0,
          label: "All Lockers",
        });
      }

      setLockerName(tempData);
    }
  }, [lockerNameData, allLockersSelected]);

  useEffect(() => {
    if (selectedLockerType !== "-1") {
      fetchLockerName({
        variables: {
          operatorId: operatorId,
          lockerTypeId: selectedLockerType,
        },
      });
    }
  }, [selectedLockerType]);

  useEffect(() => {
    if (filterButtonClicked) {
      const lockerIdValues = selectedLockerName.map((locker) => locker.value);
      // 58
      fetchOverstayReportSummary({
        variables: {
          operatorId: localStorage.getItem("user")
            ? JSON.parse(localStorage.getItem("user")).operator_id
            : "",
          transactionDateStart: toUTCDateTimeString(
            new Date(filterVariables.startDate),
            filterVariables.startTime || "00:00"
          ),
          transactionDateEnd: toUTCDateTimeString(
            new Date(filterVariables.endDate),
            filterVariables.endTime || "23:59"
          ),
          lockerIds: lockerIdValues,
        },
      });

      setFilterButtonClicked(false);
    }
  }, [filterButtonClicked]);

  const handleSearchResultHeader = () => {
    let lockerNameText;
    if (selectedLockerName.length === lockerName.length) {
      lockerNameText = "ALL Lockers";
    } else {
      const lockerLabels = selectedLockerName
        .map((selectedLocker) => {
          const matchedLocker = lockerName.find(
            (item) => parseInt(item.value) === parseInt(selectedLocker.value)
          );
          return matchedLocker ? matchedLocker.label : null;
        })
        .filter((label) => label !== null);

      lockerNameText =
        lockerLabels.length > 0 ? lockerLabels.join(", ") : "ALL Lockers";
    }

    setSearchResultHeader(
      `${
        selectedLockerType !== "-1"
          ? lockerType.find((x) => x.value === selectedLockerType).name
          : "ALL Locker Types"
      },  ${lockerNameText}, from ${formatDate(
        filterVariables.startDate
      )} to ${formatDate(filterVariables.endDate)}`
    );
  };

  const handleSearch = () => {
    // setIsClicked(true);
    // setFilterButtonClicked(true);
    // setIsLoading(true);
    // setOverstaySummaryTable([]);
    handleSearchResultHeader();
    if (filterVariables.startDate > filterVariables.endDate) {
      showError("Invalid Date", "Start date must be before end date.");
      setFilterButtonClicked(false);
      setIsClicked(false);
      return;
    }
    //date range picker validation
    if (filterVariables.startDate == "" && filterVariables.endDate == "") {
      showError("Invalid Date", "No date range selected");
      setFilterButtonClicked(false);

      setIsLoading(false);
      setIsClicked(false);

      return;
    } else if (filterVariables.endDate == "") {
      showError("Invalid Date", "No end date was selected");
      setFilterButtonClicked(false);

      setIsLoading(false);
      setIsClicked(false);

      return;
    } else if (filterVariables.startDate == "") {
      showError("Invalid Date", "No start date was selected");
      setFilterButtonClicked(false);

      setIsLoading(false);
      setIsClicked(false);

      return;
    }

    // uncomment pag may lockerid na
    if (selectedLockerType !== "-1" && selectedLockerName.length === 0) {
      showError(
        "Incomplete search criteria",
        "Locker Name is a required field"
      );
      setFilterButtonClicked(false);

      setIsLoading(false);
      setIsClicked(false);

      return;
    } else {
      setIsClicked(true);
      setFilterButtonClicked(true);
    }
  };
  useEffect(() => {
    if (
      overstaySummaryLazy &&
      overstaySummaryLazy.lockerPackageSummary.length > 0
    ) {
      setIsLoading(true);

      const data = overstaySummaryLazy.lockerPackageSummary.map((data) => {
        return {
          lockerId: data.lockerId,
          lockerName: findLockerName(data.lockerId, nameData),
          // // percentage: pod.totalPayments / (pod.totalPayments)"-",
          paymentCurrency: data.paymentCurrency,
          earliestPackage: formatDateTime(data.earliestPackage),
          latestPackage: formatDateTime(data.latestPackage),
          totalPackages: data.packages,
          packages: data.packages,
          // totalPrice: pod.totalPrice,
          // totalFees: data.totalFees,
          totalPayments: data.totalPayments,
          currencyId: data.paymentCurrency === "PHP" ? 1 : 2,
          percentPerLockerPayments: data.percentPerLockerPayments.toFixed(2),
          percentPerLockerPackages: data.percentPerLockerPackages.toFixed(2),
        };
      });

      setOverstaySummaryTable(data);
      setIsLoading(false);
      setFilterButtonClicked(false);
    } else {
      setOverstaySummaryTable([]);
      setGroupedData([]);
      setIsLoading(false);
    }
  }, [overstaySummaryLazy]);

  useEffect(() => {
    if (overstaySummaryTable && overstaySummaryTable.length > 0) {
      // Group data by paymentCurrency
      const groupedData = overstaySummaryTable.reduce((acc, item) => {
        const { paymentCurrency } = item;

        if (!acc[paymentCurrency]) {
          acc[paymentCurrency] = {
            data: [],
            totalPayments: 0,
            totalPackages: 0,
          };
        }

        // Add item to the currency's data array
        acc[paymentCurrency].data.push(item);

        // Update totals for the currency
        acc[paymentCurrency].totalPayments += item.totalPayments;
        acc[paymentCurrency].totalPackages += item.totalPackages;

        return acc;
      }, {});

      // Add percentage calculation for each locker within each currency
      Object.entries(groupedData).forEach(([currency, group]) => {
        const { totalPayments } = group;

        group.data = group.data.map((item) => ({
          ...item,
          percentPerLocker:
            totalPayments > 0
              ? ((item.totalPayments / totalPayments) * 100).toFixed(2) + "%"
              : "0%",
        }));
      });

      // Update state with grouped data
      setGroupedData(groupedData);
    }
  }, [overstaySummaryTable]);
  const toggleTableVisiblity = () => {
    setShowMore(!showMore);
  };

  // handle change filter variables
  const handleFilterChange = (e, field) => {
    setIsClicked(false);
    let value = e.target.value;

    

    if (field === "startDate" || field === "endDate") {
      value = value ? new Date(value) : null;
    }

    setFilterVariables({
      ...filterVariables,
      [field]: value,
    });
  };

  const getFooterValue = (currencyId, totalPayments) => {
    if (currencyId === 2) {
      return `${totalPayments} MBOX Coins`;
    } else {
      return `₱ ${totalPayments} `;
    }
  };

  const headerGroup = (data) => {
    return (
      <ColumnGroup>
        <Row>
          <Column
            field="lockerName"
            header="Locker Name"
            rowSpan={2}
            sortable
            alignHeader={"center"}
            headerStyle={{
              backgroundColor: "#FECD08",
              color: "black",
              border: "2px solid rgb(168 162 158)",
            }}
          />

          {/* <Column
					header="%"
					field="percentPerLocker"
					rowSpan={2}
					headerStyle={{ backgroundColor: "#FECD08" }}
				/> */}
          <Column
            field=""
            header="Total Packages"
            colSpan={2}
            alignHeader={"center"}
            headerStyle={{
              backgroundColor: "#FECD08",
              color: "black",
              border: "2px solid rgb(168 162 158)",
            }}
          />
          {showMore && (
            <Column
              header={`(Package) Oldest Record`}
              field="earliestPackage"
              rowSpan={2}
              alignHeader={"center"}
              className="text-center"
              headerStyle={{
                backgroundColor: "#FECD08",
                color: "black",
                border: "2px solid rgb(168 162 158)",
              }}
            />
          )}

          {showMore && (
            <Column
              header={` (Package) Newest Record`}
              field="latestPackage"
              rowSpan={2}
              alignHeader={"center"}
              className="text-center"
              headerStyle={{
                backgroundColor: "#FECD08",
                color: "black",
                border: "2px solid rgb(168 162 158)",
              }}
            />
          )}

          <Column
            field=""
            header="Total Payments"
            colSpan={2}
            alignHeader={"center"}
            headerStyle={{
              backgroundColor: "#FECD08",
              color: "black",
              border: "2px solid rgb(168 162 158)",
            }}
          />
        </Row>
        <Row>
          <Column
            field="totalPackages"
            header="Total Packages"
            sortable
            colSpan={1}
            alignHeader={"center"}
            headerStyle={{
              backgroundColor: "#facc15",
              color: "black",
              border: "2px solid rgb(168 162 158)",
            }}
          />
          <Column
            field="totalPackages"
            header="%"
            sortable
            colSpan={1}
            alignHeader={"center"}
            headerStyle={{
              backgroundColor: "#facc15",
              color: "black",
              border: "2px solid rgb(168 162 158)",
            }}
          />

          <Column
            field="totalPayments"
            header="Total Payments"
            colSpan={1}
            sortable
            alignHeader={"center"}
            headerStyle={{
              backgroundColor: "#facc15",
              color: "black",
              border: "2px solid rgb(168 162 158)",
            }}
          />
          <Column
            field="totalPayments"
            header="%"
            sortable
            colSpan={1}
            alignHeader={"center"}
            headerStyle={{
              backgroundColor: "#facc15",
              color: "black",
              border: "2px solid rgb(168 162 158)",
            }}
          />
        </Row>
      </ColumnGroup>
    );
  };

  const footerGroup = (data) => {
    return (
      <ColumnGroup>
        <Row>
          <Column
            footer="Totals:"
            colSpan={1}
            footerStyle={{
              textAlign: "right",
              border: "2px solid rgb(168 162 158)",
            }}
            className="bg-primary-primaryYellowLight"
          />
          <Column
            colSpan={2}
            footerStyle={{
              border: "2px solid rgb(168 162 158)",
            }}
            footer={`${data.totalPackages} packages`}
            className="text-center bg-primary-primaryYellowLight"
          />

          {showMore && (
            <Column
              className="text-center bg-primary-primaryYellowLight"
              footerStyle={{
                border: "2px solid rgb(168 162 158)",
              }}
            />
          )}
          {showMore && (
            <Column
              className="text-center bg-primary-primaryYellowLight"
              footerStyle={{
                border: "2px solid rgb(168 162 158)",
              }}
            />
          )}
          {/* <Column
					footer={`₱${totalPrice}`}
					className="text-center bg-primary-primaryYellowLight"
				/>
				<Column
					footer={`₱${totalFees}`}
					className="text-center bg-primary-primaryYellowLight"
				/> */}
          <Column
            footer={getFooterValue(data.data[0].currencyId, data.totalPayments)}
            colSpan={2}
            footerStyle={{
              border: "2px solid rgb(168 162 158)",
            }}
            className="text-center bg-primary-primaryYellowLight"
          />

          {/* <Column footer={totalAmt} /> */}
        </Row>
      </ColumnGroup>
    );
  };

  return (
    <>
      <ContentContainer containerTitle={"Overstay Fees Report Summary"}>
        <div className="px-4">
          <h2 className={"font-bold"}>Locker:</h2>
          <div className={"grid grid-cols-8 items-center w-full gap-y-2"}>
            <div className="col-span-6 grid">
              <div className="grid grid-cols-3 w-full items-center">
                <div className={"col-span-1"}>Locker Type:</div>
                <Select
                  defaultOption={{ value: "-1", name: "--- ALL ---" }}
                  items={lockerType}
                  className={"col-span-2"}
                  value={selectedLockerType}
                  onChange={(event) => {
                    setAllLockersSelected(false);
                    setSelectedLockerName([]);

                    setSelectedLockerType(event.target.value);
                  }}
                />
              </div>
            </div>
            <div className="col-span-2"></div>
            <div className="col-span-6 grid">
              <div className="grid grid-cols-3 w-full items-center">
                <div className={"col-span-1"}>Locker Name:</div>

                <MultiSelect
                  // className=""
                  placeholder={`${
                    selectedLockerType === "-1" ? "ALL Lockers" : "Select..."
                  }`}
                  items={lockerName}
                  disabled={selectedLockerType === "-1"}
                  // defaultOption={{ value: "-1", name: "-- ALL --" }}
                  value={selectedLockerName}
                  className={"col-span-2"}
                  onChange={(event) => {
                    setSelectedLockerName(event.target.value);
                  }}
                />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-5 pt-[8px]">
            <div className={"font-bold col-span-5"}>Payment Date filter:</div>

            <PrimereactDateRange
              className={`col-span-4`}
              startDate={filterVariables.startDate}
              endDate={filterVariables.endDate}
              startTime={filterVariables.startTime}
              endTime={filterVariables.endTime}
              startDateName="startDate"
              endDateName="endDate"
              startTimeName="startTime"
              endTimeName="endTime"
              onChange={(event, fieldName) => {
                handleFilterChange(event, fieldName);
              }}
              readOnlyInput={true}
              max={new Date()}
              min={maxStartDate}
              timeOnly={true}
            />
          </div>

          <div className="grid grid-cols-2 w-1/2 gap-y-2">
            <div></div>
            <u
              className="flex hover:text-blue-500 cursor-pointer mt-4 ml-auto w-min"
              onClick={() => {
                setFilterVariables({
                  ...filterVariables,
                  startDate: maxStartDate,
                  endDate: new Date(),
                  endTime: "23:59",
                  startTime: "00:00",
                });
                setSelectedLockerType("-1");
                setSelectedLockerName([]);
              }}
            >
              <i className="fas fa-undo my-auto"></i>
              <p className="ml-1">Reset</p>
            </u>
          </div>
        </div>

        <Button
          value={"Search"}
          // disabled={isLoading}
          onClick={handleSearch}
          className={"w-40 h-8 m-5"}
        />

        {isClicked &&
          (isLoading ? (
            <LoadingComponent />
          ) : (
            // <Card
            // 	className={`${overstaySummaryTable.length === 0 ? "hidden" : ""}`}
            // >
            // 	{/* <DownloadButton dt={dt} csvOnly={true} /> */}
            // 	{/* <div className={" bg-primary-primaryYellow p-2 "}>
            // 		<div className={"text-center"}>
            // 			<span>
            // 				<h1 className={"font-bold text-lg"}>POD SUMMARY REPORT</h1>
            // 			</span>
            // 		</div>
            // 	</div> */}

            // </Card>
            <>
              <div className="py-4 space-y-5">
                {Object.entries(groupedData).map(([currency, group]) => (
                  <Card className="pb-4" key={currency}>
                    <div>
                      <div>
                        <Checkbox
                          checked={showMore}
                          onChange={toggleTableVisiblity}
                          label="Show More Details"
                        />
                      </div>
                      <DownloadButton dt={dt} csvOnly={true} />

                      <div className="bg-primary-primaryYellow p-2">
                        <div className="text-center">
                          <span>
                            <h1 className={"font-bold text-lg text-black"}>
                              {currency === "PHMC" ? "MBOX COINS" : "PHP"} Table
                            </h1>
                          </span>
                          <span>
                            <h1
                              className={"font-bold text-lg text-black"}
                            >{`For ${searchResultHeader} `}</h1>
                          </span>
                        </div>
                      </div>

                      <DataTable
                        value={group.data}
                        sortMode="multiple"
                        stripedRows
                        exportFilename={`${currency} Overstay Summary Report`}
                        tableStyle={{
                          minWidth: "50rem",
                          borderWidth: 2,
                          borderColor: "#E6E6E6",
                        }}
                        removableSort
                        ref={dt}
                        footerColumnGroup={footerGroup(group)}
                        headerColumnGroup={headerGroup(group)}
                      >
                        <Column
                          header="Locker Name"
                          field="lockerName"
                          sortable
                          alignHeader="center"
                          className="font-semibold border-x-2 border-b-0 border-x-[#a8a29e]"
                          headerStyle={{ backgroundColor: "#FECD08" }}
                        />

                        <Column
                          header="Total Packages"
                          field="totalPackages"
                          sortable
                          alignHeader="center"
                          className="text-center"
                          headerStyle={{ backgroundColor: "#FECD08" }}
                        />
                        <Column
                          header={`%s`}
                          field="percentPerLockerPackages"
                          alignHeader={"center"}
                          className="text-center border-x-2 border-b-0 border-x-[#a8a29e]"
                          headerStyle={{ backgroundColor: "#FECD08" }}
                          body={(rowData) => {
                            return (
                              <div
                                className={`${
                                  rowData.percentPerLockerPackages > 50.0
                                    ? "text-green-600"
                                    : "text-orange-400"
                                }`}
                              >
                                {rowData.percentPerLockerPackages}%
                              </div>
                            );
                          }}
                        />

                        {showMore && (
                          <Column
                            header={`(Package) Oldest Record`}
                            field="earliestPackage"
                            sortable
                            alignHeader={"center"}
                            className="text-center border-x-2 border-b-0 border-x-[#a8a29e]"
                            headerStyle={{ backgroundColor: "#FECD08" }}
                          />
                        )}

                        {showMore && (
                          <Column
                            header={`(Package) Newest Record`}
                            field="latestPackage"
                            sortable
                            alignHeader={"center"}
                            className="text-center border-x-2 border-b-0 border-x-[#a8a29e]"
                            headerStyle={{ backgroundColor: "#FECD08" }}
                          />
                        )}
                        <Column
                          header="Total Payments"
                          field="totalPayments"
                          alignHeader="center"
                          className="text-center"
                          headerStyle={{ backgroundColor: "#FECD08" }}
                          body={(rowData) => (
                            <div>
                              {getFooterValue(
                                rowData.currencyId,
                                rowData.totalPayments
                              )}
                            </div>
                          )}
                        />

                        <Column
                          header={`%`}
                          field="percentPerLockerPayments"
                          alignHeader={"center"}
                          className="text-center border-x-2 border-b-0 border-x-[#a8a29e]"
                          headerStyle={{ backgroundColor: "#FECD08" }}
                          body={(rowData) => {
                            return (
                              <div
                                className={`${
                                  rowData.percentPerLockerPayments > 50.0
                                    ? "text-green-600"
                                    : "text-orange-400"
                                }`}
                              >
                                {rowData.percentPerLockerPayments}%
                              </div>
                            );
                          }}
                        />
                      </DataTable>
                    </div>
                  </Card>
                ))}
              </div>
            </>
          ))}

        {!isLoading && isClicked && groupedData.length === 0 && (
          <NoDataFoundTemplate />
        )}

        <Modal
          icon={validation.icon}
          title={validation.title}
          message={validation.message}
          open={validation.open}
          actions={validation.actions}
        />
      </ContentContainer>
    </>
  );
};

export default OverstayFeesReportSummary;
